/// <reference lib="dom" />
//
// Project Planner island.
//
// Draw pipe runs (straight polylines) on a Leaflet map, group them into one or
// more configurations, assign each line a pipe variant from the catalogue, and
// price it as length × price/metre. Everything is client-side; the drawing is
// persisted to localStorage. Pipe data is embedded in the page as JSON.
//
// Straight lines only for now — Geoman gives us editable vertices, and the
// per-line model leaves room for curved segment types later.

import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";

export {}; // module scope

// Tiles are native to ~z19; we allow zooming closer (upscaled) for precise drawing.
const MAX_ZOOM = 22;

// Geodetic-height warning: flag networks whose elevation spread exceeds this many
// metres (static head from this much water already approaches typical pipe ratings).
const HEIGHT_WARN_M = 30;
const BAR_PER_M_WATER = 0.0981; // ρgh for water, in bar per metre

// ---------------------------------------------------------------------------
// Types (mirror PlannerData on the server)
// ---------------------------------------------------------------------------

type PipeVariant = {
  variantId: number;
  sku: string;
  label: string;
  pricePerMeterCents: number;
  currency: string;
  fallback: boolean;
};
type Pipe = {
  slug: string;
  name: string;
  brand: string | null;
  minBendRadiusM: number | null;
  maxBendDeg: number | null;
  variants: PipeVariant[];
};
type Corner = {
  sku: string;
  label: string;
  brand: string | null;
  sizeMm: number | null;
  priceCents: number;
  currency: string;
};
type PlannerData = {
  pipes: Pipe[];
  corners: Corner[];
  cornerFallback: { label: string; priceCents: number; currency: string };
  currency: string;
  lang: string;
};

type LineRec = {
  id: string;
  configId: string;
  sku: string | null;
  latlngs: [number, number][];
  // Terrain-following (3D) length in metres from the swisstopo elevation profile.
  // null = looked up but unavailable (outside Switzerland); undefined = not yet fetched.
  terrainMeters?: number | null;
  // Elevation extremes along the line (metres) and their map positions [lat,lng].
  minAlt?: number | null;
  maxAlt?: number | null;
  minPt?: [number, number] | null;
  maxPt?: [number, number] | null;
};
type ConfigRec = { id: string; name: string };
type Persisted = {
  configs: ConfigRec[];
  lines: LineRec[];
  activeConfigId: string;
  activePipeSku?: string | null; // pipe pre-selected for newly drawn lines
  terrain?: boolean; // account for elevation (Swiss data) — defaults on
  view?: { center: [number, number]; zoom: number; base: "esri" | "osm" | "vector" };
};

// ---------------------------------------------------------------------------
// Boot guard + data
// ---------------------------------------------------------------------------

const mapEl = document.getElementById("planner-map");
const dataEl = document.getElementById("planner-data");
const configsEl = document.getElementById("planner-configs");
const totalEl = document.getElementById("planner-total");
if (mapEl && dataEl && configsEl && totalEl) {
  boot(mapEl, dataEl, configsEl, totalEl);
}

function boot(
  mapEl: HTMLElement,
  dataEl: HTMLElement,
  configsEl: HTMLElement,
  totalEl: HTMLElement,
) {
  injectLeafletCss();

  const data = parseData(dataEl);
  const pipeBySku = indexPipes(data.pipes);
  const STORAGE_KEY = "cflex:planner:v1";

  // Delegated add-all-to-cart (the totals block re-renders its innerHTML).
  totalEl.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-add-all-cart]") as HTMLButtonElement | null;
    if (btn && !btn.disabled) void addAllToCart(btn);
  });

  // ---- palette for per-configuration colours ----
  const PALETTE = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed", "#0891b2"];

  // ---- load persisted state (or seed a default configuration) ----
  let state = loadState();
  if (state.configs.length === 0) {
    const id = genId();
    state = { configs: [{ id, name: "Configuration 1" }], lines: [], activeConfigId: id };
  }
  if (!state.configs.some((c) => c.id === state.activeConfigId)) {
    state.activeConfigId = state.configs[0]!.id;
  }

  // ---------------------------------------------------------------------------
  // Map + layers
  // ---------------------------------------------------------------------------

  const esri = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: MAX_ZOOM, maxNativeZoom: 19, attribution: "Imagery © Esri" },
  );
  const osm = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: MAX_ZOOM,
    maxNativeZoom: 19, // upscale native tiles past z19 so we can zoom in closer
    attribution: "© OpenStreetMap contributors",
  });
  // Place/road labels to overlay on top of satellite imagery (worldwide).
  const labels = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: MAX_ZOOM, maxNativeZoom: 19, attribution: "Labels © Esri" },
  );

  const view = state.view;
  const preferredBase = view?.base ?? "vector"; // crisp vector by default
  const map = L.map(mapEl, {
    center: view?.center ?? [47.6, 13.35], // Austria-ish default
    zoom: view?.zoom ?? 7,
    maxZoom: MAX_ZOOM,
    zoomControl: false, // removed per design; pinch / scroll / +- keys still zoom
    // Vector loads async; start on satellite/OSM and switch to vector once ready.
    layers: preferredBase === "esri" ? [esri, labels] : [osm],
  });

  const layersCtl = L.control
    .layers(
      { "Map (OpenStreetMap)": osm, "Satellite": esri },
      { "Place labels": labels },
      { collapsed: true, position: "bottomright" },
    )
    .addTo(map);

  // Vector basemap (MapLibre GL) loaded from CDN — crisp at any zoom.
  let vectorLayer: L.Layer | null = null;
  function currentBaseName(): "osm" | "esri" | "vector" {
    if (vectorLayer && map.hasLayer(vectorLayer)) return "vector";
    if (map.hasLayer(esri)) return "esri";
    return "osm";
  }
  loadMapLibre()
    .then(() => {
      vectorLayer = (L as any).maplibreGL({
        style: "https://tiles.openfreemap.org/styles/liberty",
        attribution: "© OpenFreeMap © OpenMapTiles © OpenStreetMap contributors",
      });
      layersCtl.addBaseLayer(vectorLayer!, "Vector (crisp)");
      if (preferredBase === "vector") {
        if (map.hasLayer(osm)) map.removeLayer(osm);
        vectorLayer!.addTo(map);
      }
    })
    .catch(() => {
      /* CDN blocked — raster layers remain */
    });

  // Labels only make sense over satellite; toggle them with the base layer.
  map.on("baselayerchange", (e: L.LayersControlEvent) => {
    if (e.layer === esri) {
      if (!map.hasLayer(labels)) labels.addTo(map);
    } else {
      if (map.hasLayer(labels)) map.removeLayer(labels);
    }
    saveSoon();
  });

  setTimeout(() => map.invalidateSize(), 0);
  window.addEventListener("resize", () => map.invalidateSize());

  // Collapse / expand the floating configurations panel.
  const toggleBtn = document.getElementById("planner-toggle-panel");
  const panelBody = document.getElementById("planner-panel-body");
  toggleBtn?.addEventListener("click", () => {
    const collapsed = panelBody?.classList.toggle("hidden");
    const icon = toggleBtn.querySelector("i");
    if (icon) {
      icon.classList.toggle("fa-chevron-up", !collapsed);
      icon.classList.toggle("fa-chevron-down", !!collapsed);
    }
  });

  // Per-line terrain fetch state (not persisted): "loading" | "ok" | "unavailable".
  const terrainStatus = new Map<string, "loading" | "ok" | "unavailable">();
  const terrainTimers = new Map<string, number>();
  const elevMarkers = new Map<string, L.LayerGroup>(); // high/low markers per line
  const terrainEnabled = () => state.terrain !== false;

  // Terrain (Swiss elevation) toggle.
  const terrainToggle = document.getElementById("planner-terrain") as HTMLInputElement | null;
  if (terrainToggle) {
    terrainToggle.checked = terrainEnabled();
    terrainToggle.addEventListener("change", () => {
      state.terrain = terrainToggle.checked;
      if (state.terrain) {
        for (const line of state.lines) {
          if (line.terrainMeters == null) scheduleTerrain(line);
          else renderElevMarkers(line);
        }
      } else {
        clearElevMarkers();
      }
      renderPanel();
      saveSoon();
    });
  }

  // Active pipe picker (side panel): sets the pipe used for newly drawn lines.
  const pipeEl = document.getElementById("planner-pipe");
  pipeEl?.addEventListener("change", (e) => {
    const sel = e.target as HTMLSelectElement;
    if (sel.matches("select[data-active-pipe]")) {
      state.activePipeSku = sel.value || null;
      renderPipePicker();
      saveSoon();
    }
  });

  const pm = (map as any).pm; // Geoman plugin surface

  // ---------------------------------------------------------------------------
  // Drawn-line layers
  // ---------------------------------------------------------------------------

  // Each line has: an editable faint "route" polyline (Geoman edits this), a bold
  // "curve" polyline showing how the pipe actually lies (fillets/elbows), and
  // elbow markers at sharp corners.
  const layerByLineId = new Map<string, L.Polyline>(); // route (editable)
  const curveByLineId = new Map<string, L.Polyline>(); // display curve
  const elbowByLineId = new Map<string, L.LayerGroup>();

  function colorForConfig(configId: string): string {
    const idx = Math.max(0, state.configs.findIndex((c) => c.id === configId));
    return PALETTE[idx % PALETTE.length]!;
  }

  function addLayerForLine(line: LineRec): L.Polyline {
    const route = L.polyline(line.latlngs, {
      color: colorForConfig(line.configId),
      weight: 2,
      opacity: 0.45,
      dashArray: "4 6",
    }).addTo(map);
    layerByLineId.set(line.id, route);
    bindLayerEditing(route, line.id);
    updateCurve(line);
    return route;
  }

  // Redraw the bold pipe curve + elbow markers for one line, from its selected pipe.
  function updateCurve(line: LineRec) {
    const { path, elbowVertices } = computeBend(line);
    const color = colorForConfig(line.configId);
    let curve = curveByLineId.get(line.id);
    if (!curve) {
      curve = L.polyline(path, { color, weight: 5, opacity: 0.95, interactive: false }).addTo(map);
      curveByLineId.set(line.id, curve);
    } else {
      curve.setLatLngs(path).setStyle({ color });
    }
    elbowByLineId.get(line.id)?.remove();
    elbowByLineId.delete(line.id);
    if (elbowVertices.length) {
      const markers = elbowVertices.map((i) =>
        L.circleMarker(line.latlngs[i]!, {
          radius: 4, color: "#fff", weight: 2, fillColor: "#f59e0b", fillOpacity: 1, interactive: false,
        }).bindTooltip("elbow", { direction: "top" }),
      );
      elbowByLineId.set(line.id, L.layerGroup(markers).addTo(map));
    }
  }

  function removeLineLayers(lineId: string) {
    layerByLineId.get(lineId)?.remove();
    layerByLineId.delete(lineId);
    curveByLineId.get(lineId)?.remove();
    curveByLineId.delete(lineId);
    elbowByLineId.get(lineId)?.remove();
    elbowByLineId.delete(lineId);
    elevMarkers.get(lineId)?.remove();
    elevMarkers.delete(lineId);
  }

  function bindLayerEditing(poly: L.Polyline, lineId: string) {
    // Recompute whenever the user drags/adds/removes a vertex in edit mode.
    poly.on("pm:edit", () => {
      const rec = state.lines.find((l) => l.id === lineId);
      if (!rec) return;
      rec.latlngs = toLatLngPairs(poly);
      rec.terrainMeters = undefined; // geometry changed — terrain length is stale
      updateCurve(rec);
      scheduleTerrain(rec);
      renderPanel();
      saveSoon();
    });
  }

  // Re-create layers for persisted lines and seed terrain state.
  for (const line of state.lines) addLayerForLine(line);
  for (const line of state.lines) {
    if (line.terrainMeters != null) {
      terrainStatus.set(line.id, "ok");
      renderElevMarkers(line);
    } else if (line.terrainMeters === null) {
      terrainStatus.set(line.id, "unavailable");
    } else if (terrainEnabled()) {
      scheduleTerrain(line); // undefined => fetch
    }
  }
  enableEditing();

  function enableEditing() {
    // Draggable vertices on every line. Geoman manages draw vs edit internally.
    pm.enableGlobalEditMode({ allowSelfIntersection: true });
  }

  // ---------------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------------

  const drawBtn = document.getElementById("planner-draw");
  const hintEl = document.getElementById("planner-hint");

  drawBtn?.addEventListener("click", () => {
    pm.disableGlobalEditMode();
    pm.enableDraw("Line", { continueDrawing: false, finishOn: "dblclick" });
    setHint("Click to add points · double-click to finish");
  });

  map.on("pm:create", (e: any) => {
    const poly = e.layer as L.Polyline;
    const latlngs = toLatLngPairs(poly);
    pm.disableDraw();
    setHint("");

    if (latlngs.length < 2) {
      map.removeLayer(poly);
      enableEditing();
      return;
    }

    const line: LineRec = {
      id: genId(),
      configId: state.activeConfigId,
      sku: state.activePipeSku ?? null, // use the pipe chosen in the side panel
      latlngs,
    };
    state.lines.push(line);
    poly.setStyle({ color: colorForConfig(line.configId), weight: 2, opacity: 0.45, dashArray: "4 6" });
    layerByLineId.set(line.id, poly);
    bindLayerEditing(poly, line.id);
    updateCurve(line);

    enableEditing();
    scheduleTerrain(line);
    renderPanel();
    saveSoon();
  });

  // ---------------------------------------------------------------------------
  // Place search (Nominatim / OpenStreetMap)
  // ---------------------------------------------------------------------------

  const searchEl = document.getElementById("planner-search") as HTMLInputElement | null;
  const resultsEl = document.getElementById("planner-search-results");
  if (searchEl && resultsEl) wireSearch(searchEl, resultsEl, map);

  // ---------------------------------------------------------------------------
  // Panel: configurations, lines, totals
  // ---------------------------------------------------------------------------

  document.getElementById("planner-add-config")?.addEventListener("click", () => {
    const id = genId();
    state.configs.push({ id, name: `Configuration ${state.configs.length + 1}` });
    state.activeConfigId = id;
    renderPanel();
    saveSoon();
  });

  document.getElementById("planner-clear")?.addEventListener("click", () => {
    if (!confirm("Remove all configurations and drawn lines?")) return;
    for (const id of [...layerByLineId.keys()]) removeLineLayers(id);
    const id = genId();
    state = { configs: [{ id, name: "Configuration 1" }], lines: [], activeConfigId: id, terrain: state.terrain, activePipeSku: state.activePipeSku };
    renderPanel();
    saveSoon();
  });

  // Event delegation for the dynamically-rendered panel.
  configsEl.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    const action = target.closest<HTMLElement>("[data-action]");
    if (action) {
      const a = action.dataset.action;
      const lineId = action.dataset.line;
      const configId = action.dataset.config;
      if (a === "delete-line" && lineId) deleteLine(lineId);
      else if (a === "zoom-line" && lineId) zoomToLine(lineId);
      else if (a === "delete-config" && configId) deleteConfig(configId);
      return;
    }
    const card = target.closest<HTMLElement>("[data-set-active]");
    if (card) {
      const id = card.dataset.setActive;
      if (id && id !== state.activeConfigId) {
        state.activeConfigId = id;
        renderPanel();
        saveSoon();
      }
    }
  });

  configsEl.addEventListener("change", (ev) => {
    const sel = ev.target as HTMLSelectElement;
    if (sel.matches("select[data-line-sku]")) {
      const lineId = sel.dataset.lineSku;
      const rec = state.lines.find((l) => l.id === lineId);
      if (rec) {
        rec.sku = sel.value || null;
        updateCurve(rec); // bend geometry depends on the selected pipe
        renderPanel();
        saveSoon();
      }
    }
  });

  function deleteLine(lineId: string) {
    removeLineLayers(lineId);
    state.lines = state.lines.filter((l) => l.id !== lineId);
    renderPanel();
    saveSoon();
  }

  function zoomToLine(lineId: string) {
    const poly = layerByLineId.get(lineId);
    if (poly) map.fitBounds(poly.getBounds(), { maxZoom: 19, padding: [40, 40] });
  }

  function deleteConfig(configId: string) {
    const lines = state.lines.filter((l) => l.configId === configId);
    if (lines.length && !confirm(`Delete this configuration and its ${lines.length} line(s)?`)) {
      return;
    }
    for (const l of lines) removeLineLayers(l.id);
    state.lines = state.lines.filter((l) => l.configId !== configId);
    state.configs = state.configs.filter((c) => c.id !== configId);
    if (state.configs.length === 0) {
      const id = genId();
      state.configs.push({ id, name: "Configuration 1" });
      state.activeConfigId = id;
    } else if (state.activeConfigId === configId) {
      state.activeConfigId = state.configs[0]!.id;
    }
    // Colours are index-based, so refresh existing line styles.
    recolorAll();
    renderPanel();
    saveSoon();
  }

  function recolorAll() {
    for (const line of state.lines) {
      const color = colorForConfig(line.configId);
      layerByLineId.get(line.id)?.setStyle({ color });
      curveByLineId.get(line.id)?.setStyle({ color });
    }
  }

  // ---- rendering -----------------------------------------------------------

  const money = makeMoneyFormatters();
  let fallbackSeen = false;

  function renderPanel() {
    fallbackSeen = false;
    configsEl.innerHTML = state.configs.map(renderConfig).join("");
    totalEl.innerHTML = renderTotals();
  }

  // Active-pipe selector + its bend specs. Rendered separately so panel re-renders
  // (e.g. on terrain updates) don't reset an open dropdown.
  function renderPipePicker() {
    if (!pipeEl) return;
    const sku = state.activePipeSku ?? null;
    const entry = sku ? pipeBySku.get(sku) : undefined;
    let spec = `<p class="mt-1 text-[11px] text-slate-400">Pick a pipe — newly drawn lines use it.</p>`;
    if (entry) {
      const p = entry.pipe;
      const price = money(entry.variant.currency).format(entry.variant.pricePerMeterCents / 100);
      const bend =
        p.minBendRadiusM && p.minBendRadiusM > 0
          ? `↪ min radius ${p.minBendRadiusM} m · max bend ${p.maxBendDeg ?? 180}°`
          : `<span class="text-amber-600">rigid — elbow at every corner</span>`;
      spec = `<div class="mt-1 text-[11px] leading-tight">
        <div class="text-emerald-700">${bend}</div>
        <div class="text-slate-600">${price}/m</div>
      </div>`;
    }
    pipeEl.innerHTML = `
      <span class="label flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
        <i class="fa-solid fa-droplet"></i> Active pipe
      </span>
      <select data-active-pipe class="input-sm mt-1 w-full">${renderPipeOptions(sku)}</select>
      ${spec}`;
  }

  function renderConfig(config: ConfigRec): string {
    const lines = state.lines.filter((l) => l.configId === config.id);
    const isActive = config.id === state.activeConfigId;
    const color = colorForConfig(config.id);
    const subtotalCents = lines.reduce((sum, l) => sum + lineCostCents(l), 0);
    const totalMeters = lines.reduce((sum, l) => sum + pipeMeters(l), 0);

    const rows = lines.length
      ? lines.map((l, i) => renderLine(l, i + 1)).join("")
      : `<p class="text-xs text-slate-400">No lines yet — pick this config, then "Draw pipe".</p>`;

    return `
      <section data-set-active="${config.id}"
        class="rounded-md border ${isActive ? "border-brand-400 ring-1 ring-brand-200" : "border-slate-200"} p-3 cursor-pointer">
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-2 font-medium text-slate-800">
            <span class="inline-block h-3 w-3 rounded-sm" style="background:${color}"></span>
            ${escapeHtml(config.name)}
            ${isActive ? '<span class="text-[10px] uppercase tracking-wide text-brand-600">active</span>' : ""}
          </span>
          <button type="button" data-action="delete-config" data-config="${config.id}"
            class="text-xs text-slate-400 hover:text-red-600" title="Delete configuration">✕</button>
        </div>
        <div class="mt-2 space-y-2">${rows}</div>
        <div class="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-600">
          <span>${fmtMeters(totalMeters)}</span>
          <span class="font-semibold text-slate-800">${money(data.currency).format(subtotalCents / 100)}</span>
        </div>
      </section>`;
  }

  function renderLine(line: LineRec, n: number): string {
    const planar = lineMeters(line);
    const pipeLen = pipeMeters(line);
    const bend = computeBend(line);
    const costCents = lineCostCents(line);
    const entry = line.sku ? pipeBySku.get(line.sku) : undefined;
    if (entry?.variant.fallback) fallbackSeen = true;
    const costText = entry ? money(entry.variant.currency).format(costCents / 100) : "—";
    const usesTerrain = terrainEnabled() && line.terrainMeters != null;

    // Length display: pipe length (route + bends), terrain icon when used.
    const status = terrainStatus.get(line.id);
    const mountain = `<i class="fa-solid fa-mountain text-[9px] text-emerald-600"></i>`;
    const lengthMain = `${usesTerrain ? mountain + " " : ""}${fmtMeters(pipeLen)}`;
    const subs: string[] = [];
    if (terrainEnabled()) {
      if (line.terrainMeters != null) {
        let s = `planar ${fmtMeters(planar)}`;
        if (line.minAlt != null && line.maxAlt != null) {
          const diff = (line.maxAlt - line.minAlt).toFixed(1);
          s +=
            ` · <span class="text-blue-600">▼ ${Math.round(line.minAlt)}</span>` +
            ` <span class="text-red-600">▲ ${Math.round(line.maxAlt)} m</span> · Δ ${diff} m`;
        }
        subs.push(s);
      } else if (status === "loading") {
        subs.push("measuring terrain…");
      } else if (status === "unavailable") {
        subs.push("terrain: CH only");
      }
    }
    // Bends / elbows
    if (entry) {
      const parts: string[] = [];
      if (bend.bends) parts.push(`<span class="text-emerald-600">↪ ${bend.bends} bend${bend.bends > 1 ? "s" : ""}</span>`);
      if (bend.elbowVertices.length && bend.corner) {
        const each = money(bend.corner.currency).format(bend.corner.priceCents / 100);
        const tag = bend.corner.matched ? "" : " (generic)";
        parts.push(
          `<span class="text-amber-600">⌐ ${bend.elbowVertices.length}× elbow</span>` +
            ` <span class="text-slate-400">${each} ea${tag}</span>`,
        );
      }
      if (parts.length) subs.push(parts.join(" · "));
    }

    return `
      <div class="rounded border border-slate-100 bg-slate-50 p-2">
        <div class="flex items-center justify-between text-xs">
          <span class="font-medium text-slate-700">Line ${n}</span>
          <span class="flex items-center gap-2">
            <button type="button" data-action="zoom-line" data-line="${line.id}"
              class="text-slate-400 hover:text-brand-600" title="Zoom to line">⤢</button>
            <button type="button" data-action="delete-line" data-line="${line.id}"
              class="text-slate-400 hover:text-red-600" title="Delete line">🗑</button>
          </span>
        </div>
        <div class="mt-1">
          <select data-line-sku="${line.id}" class="input-sm w-full">
            ${renderPipeOptions(line.sku)}
          </select>
        </div>
        <div class="mt-1 flex items-end justify-between text-xs text-slate-600">
          <span class="flex flex-col leading-tight">
            <span>${lengthMain}</span>
            ${subs.map((s) => `<span class="text-[10px] text-slate-400">${s}</span>`).join("")}
          </span>
          <span class="font-semibold text-slate-800">${costText}</span>
        </div>
      </div>`;
  }

  function renderPipeOptions(selectedSku: string | null): string {
    const empty = `<option value="">Select pipe…</option>`;
    const groups = data.pipes
      .map((p) => {
        const opts = p.variants
          .map((v) => {
            const sel = v.sku === selectedSku ? " selected" : "";
            const price = money(v.currency).format(v.pricePerMeterCents / 100);
            const fb = v.fallback ? " *" : "";
            return `<option value="${escapeHtml(v.sku)}"${sel}>${escapeHtml(v.label)} — ${price}/m${fb}</option>`;
          })
          .join("");
        const groupLabel = escapeHtml([p.brand, p.name].filter(Boolean).join(" · "));
        return `<optgroup label="${groupLabel}">${opts}</optgroup>`;
      })
      .join("");
    return empty + groups;
  }

  function renderTotals(): string {
    const totalCents = state.lines.reduce((sum, l) => sum + lineCostCents(l), 0);
    const totalMeters = state.lines.reduce((sum, l) => sum + pipeMeters(l), 0);
    const totalElbows = state.lines.reduce((sum, l) => sum + computeBend(l).elbowVertices.length, 0);
    const elbowNote = totalElbows
      ? `<p class="mt-1 text-[10px] text-amber-600">⌐ ${totalElbows} corner fitting${totalElbows > 1 ? "s" : ""} included</p>`
      : "";
    const usesTerrain = terrainEnabled() && state.lines.some((l) => l.terrainMeters != null);
    const terrainNote = usesTerrain
      ? `<p class="mt-1 text-[10px] text-emerald-600"><i class="fa-solid fa-mountain"></i> length follows terrain (Swiss elevation)</p>`
      : "";
    const note = fallbackSeen
      ? `<p class="mt-1 text-[10px] text-slate-400">* price shown in EUR (no ${escapeHtml(data.currency)} price set)</p>`
      : "";
    const addable = state.lines.some((l) => l.sku && pipeBySku.get(l.sku) && pipeMeters(l) > 0);
    const addBtn = addable
      ? `<button type="button" data-add-all-cart class="btn-primary mt-2 w-full text-sm"><i class="fa-solid fa-cart-plus"></i> Add all pipes to basket</button>`
      : "";
    return `
      <div class="flex items-center justify-between">
        <span class="text-slate-500">Total · ${fmtMeters(totalMeters)}</span>
        <span class="text-base font-semibold text-slate-900">${money(data.currency).format(totalCents / 100)}</span>
      </div>${elbowNote}${terrainNote}${note}${renderHeightWarning()}${addBtn}`;
  }

  // Aggregate drawn pipe lengths per variant and add each as a length cart line.
  async function addAllToCart(btn: HTMLButtonElement) {
    const byVariant = new Map<number, number>();
    for (const l of state.lines) {
      if (!l.sku) continue;
      const pipe = pipeBySku.get(l.sku);
      const m = pipeMeters(l);
      if (!pipe || m <= 0) continue;
      byVariant.set(pipe.variant.variantId, (byVariant.get(pipe.variant.variantId) ?? 0) + m);
    }
    if (byVariant.size === 0) return;
    btn.disabled = true;
    let added = 0;
    for (const [variantId, meters] of byVariant) {
      try {
        const r = await fetch("/cart/add", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
          body: new URLSearchParams({ variantId: String(variantId), qty: "1", unitAmount: String(Math.round(meters)) }),
        });
        if (r.ok) added++;
      } catch {
        /* ignore */
      }
    }
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      const c = parseInt(el.textContent || "0", 10) || 0;
      el.textContent = String(c + added);
      (el as HTMLElement).classList.remove("hidden");
    });
    btn.innerHTML = `<i class="fa-solid fa-check"></i> Added`;
    setTimeout(() => {
      btn.disabled = false;
    }, 2000);
  }

  // ---- geodetic-height warning ---------------------------------------------
  //
  // When the elevation spread between the lowest and highest measured point on
  // the *whole* network exceeds HEIGHT_WARN_M, the geodetic (static) head plus
  // operating pressure can push past the pipe's rating — flag it. The two points
  // need not be on the same line: we take the global min/max across every line's
  // extremes. (HEIGHT_WARN_M / BAR_PER_M_WATER are module-level constants.)

  function networkElevationSpread(): number | null {
    let lo = Infinity;
    let hi = -Infinity;
    for (const l of state.lines) {
      if (l.minAlt != null) lo = Math.min(lo, l.minAlt);
      if (l.maxAlt != null) hi = Math.max(hi, l.maxAlt);
    }
    if (!isFinite(lo) || !isFinite(hi)) return null;
    return hi - lo;
  }

  function renderHeightWarning(): string {
    const spread = networkElevationSpread();
    if (spread == null || spread <= HEIGHT_WARN_M) return "";
    const isDe = data.lang === "de";
    const title = isDe ? "ACHTUNG:" : "WARNING:";
    const body = isDe
      ? "Der geodätische Druck zusammen mit dem Betriebsdruck ist an manchen Stellen über 6 bar. Bitte C-flex kontaktieren für eine gute Alternative."
      : "The geodetic pressure together with the operating pressure exceeds 6 bar at some points. Please contact C-flex for a good alternative.";
    const bar = (spread * BAR_PER_M_WATER).toFixed(1);
    return `<div class="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[11px] leading-snug text-red-800">
      <i class="fa-solid fa-triangle-exclamation mr-1"></i><b>${title}</b> ${escapeHtml(body)}
      <span class="mt-1 block font-medium text-red-600">Δ ${Math.round(spread)} m · ≈ ${bar} bar ${isDe ? "geodätisch" : "geodetic"}</span>
    </div>`;
  }

  // ---- costing helpers -----------------------------------------------------

  // Planar ("as the crow flies") length via Leaflet's haversine distance.
  function lineMeters(line: LineRec): number {
    let total = 0;
    for (let i = 1; i < line.latlngs.length; i++) {
      const a = line.latlngs[i - 1]!;
      const b = line.latlngs[i]!;
      total += map.distance(a, b);
    }
    return total;
  }

  // Ground route length: terrain-following when available, else planar.
  function effectiveMeters(line: LineRec): number {
    if (terrainEnabled() && line.terrainMeters != null) return line.terrainMeters;
    return lineMeters(line);
  }

  type CornerPick = { priceCents: number; currency: string; label: string; matched: boolean };
  type BendInfo = {
    path: [number, number][];
    bendDeltaM: number;
    elbowVertices: number[];
    bends: number;
    corner: CornerPick | null;
  };

  // Geometry + fittings for a line, given its selected pipe's bend limits.
  function computeBend(line: LineRec): BendInfo {
    const entry = line.sku ? pipeBySku.get(line.sku) : undefined;
    const pipe = entry?.pipe;
    const geo = computePipePath(line.latlngs, {
      hasPipe: !!pipe,
      radiusM: pipe?.minBendRadiusM ?? null,
      maxBendDeg: pipe?.maxBendDeg ?? null,
    });
    const corner = pipe && entry?.variant ? matchCorner(pipe, entry.variant) : null;
    return { ...geo, corner };
  }

  // Pick a catalogue elbow by diameter (prefer same brand); else the generic fallback.
  function matchCorner(pipe: Pipe, variant: PipeVariant): CornerPick {
    const size = parseSizeMm(variant.label);
    if (size != null && data.corners.length) {
      const sameBrand = data.corners.filter(
        (c) => c.brand && pipe.brand && c.brand.toLowerCase() === pipe.brand.toLowerCase(),
      );
      const pool = sameBrand.length ? sameBrand : data.corners;
      const exact = pool.find((c) => c.sizeMm === size);
      const sized = pool.filter((c) => c.sizeMm != null);
      const near =
        exact ??
        (sized.length
          ? sized.sort((a, b) => Math.abs(a.sizeMm! - size) - Math.abs(b.sizeMm! - size))[0]
          : undefined);
      if (near) return { priceCents: near.priceCents, currency: near.currency, label: near.label, matched: true };
    }
    return {
      priceCents: data.cornerFallback.priceCents,
      currency: data.cornerFallback.currency,
      label: data.cornerFallback.label,
      matched: false,
    };
  }

  // Pipe length = ground route + bend adjustment (fillets shorten vs sharp corners).
  function pipeMeters(line: LineRec): number {
    return Math.max(0, effectiveMeters(line) + computeBend(line).bendDeltaM);
  }

  function lineCostCents(line: LineRec): number {
    if (!line.sku) return 0;
    const pipe = pipeBySku.get(line.sku);
    if (!pipe) return 0;
    const b = computeBend(line);
    let cents = Math.round(pipeMeters(line) * pipe.variant.pricePerMeterCents);
    cents += b.elbowVertices.length * (b.corner?.priceCents ?? 0);
    return cents;
  }

  // Debounced terrain (3D) length lookup from swisstopo for one line.
  function scheduleTerrain(line: LineRec) {
    if (!terrainEnabled() || line.latlngs.length < 2) return;
    window.clearTimeout(terrainTimers.get(line.id));
    terrainTimers.set(line.id, window.setTimeout(() => void refreshTerrain(line), 500));
  }

  async function refreshTerrain(line: LineRec) {
    if (!terrainEnabled()) return;
    terrainStatus.set(line.id, "loading");
    renderPanel();
    const res = await fetchTerrain(line.latlngs);
    if (!state.lines.find((l) => l.id === line.id)) return; // line was deleted meanwhile
    if (res) {
      line.terrainMeters = res.terrain;
      line.minAlt = res.minAlt;
      line.maxAlt = res.maxAlt;
      line.minPt = res.minPt;
      line.maxPt = res.maxPt;
      terrainStatus.set(line.id, "ok");
    } else {
      line.terrainMeters = null;
      line.minAlt = line.maxAlt = line.minPt = line.maxPt = null;
      terrainStatus.set(line.id, "unavailable");
    }
    renderElevMarkers(line);
    renderPanel();
    saveSoon();
  }

  // High/low elevation markers on the map, one group per line.
  function renderElevMarkers(line: LineRec) {
    elevMarkers.get(line.id)?.remove();
    elevMarkers.delete(line.id);
    if (!terrainEnabled() || !line.minPt || !line.maxPt || line.minAlt == null || line.maxAlt == null) return;
    const high = L.circleMarker(line.maxPt, {
      radius: 5, color: "#fff", weight: 2, fillColor: "#dc2626", fillOpacity: 1, interactive: false,
    }).bindTooltip(`▲ ${Math.round(line.maxAlt)} m`, { permanent: true, direction: "top", offset: [0, -4] });
    const low = L.circleMarker(line.minPt, {
      radius: 5, color: "#fff", weight: 2, fillColor: "#2563eb", fillOpacity: 1, interactive: false,
    }).bindTooltip(`▼ ${Math.round(line.minAlt)} m`, { permanent: true, direction: "bottom", offset: [0, 4] });
    elevMarkers.set(line.id, L.layerGroup([high, low]).addTo(map));
  }
  function clearElevMarkers() {
    for (const g of elevMarkers.values()) g.remove();
    elevMarkers.clear();
  }

  // ---- persistence ---------------------------------------------------------

  let saveTimer: number | undefined;
  function saveSoon() {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(save, 150);
  }
  function save() {
    state.view = {
      center: [map.getCenter().lat, map.getCenter().lng],
      zoom: map.getZoom(),
      base: currentBaseName(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* quota / private mode — ignore */
    }
  }
  function loadState(): Persisted {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Persisted;
        if (parsed && Array.isArray(parsed.configs) && Array.isArray(parsed.lines)) return parsed;
      }
    } catch {
      /* ignore */
    }
    return { configs: [], lines: [], activeConfigId: "" };
  }

  map.on("moveend", saveSoon);

  function setHint(text: string) {
    if (hintEl) hintEl.textContent = text;
  }

  // First paint.
  renderPipePicker();
  renderPanel();

  // ---- small utilities -----------------------------------------------------

  function makeMoneyFormatters() {
    const cache = new Map<string, Intl.NumberFormat>();
    return (currency: string) => {
      let f = cache.get(currency);
      if (!f) {
        f = new Intl.NumberFormat("de-DE", { style: "currency", currency });
        cache.set(currency, f);
      }
      return f;
    };
  }
}

// ---------------------------------------------------------------------------
// Free functions
// ---------------------------------------------------------------------------

function parseData(el: HTMLElement): PlannerData {
  const empty: PlannerData = {
    pipes: [],
    corners: [],
    cornerFallback: { label: "Corner fitting", priceCents: 0, currency: "EUR" },
    currency: "EUR",
    lang: "en",
  };
  try {
    return { ...empty, ...(JSON.parse(el.textContent || "{}") as Partial<PlannerData>) } as PlannerData;
  } catch {
    return empty;
  }
}

function indexPipes(pipes: Pipe[]) {
  const map = new Map<string, { pipe: Pipe; variant: PipeVariant }>();
  for (const pipe of pipes) {
    for (const variant of pipe.variants) map.set(variant.sku, { pipe, variant });
  }
  return map;
}

function toLatLngPairs(poly: L.Polyline): [number, number][] {
  const ll = poly.getLatLngs() as unknown[];
  const flat = (Array.isArray(ll[0]) ? (ll as L.LatLng[][]).flat() : (ll as L.LatLng[])) || [];
  return flat.map((p) => [p.lat, p.lng] as [number, number]);
}

function fmtMeters(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m.toFixed(1)} m`;
}

function parseSizeMm(s: string): number | null {
  const m = s.match(/\d+/);
  return m ? Number(m[0]) : null;
}

type PipePathResult = {
  path: [number, number][];
  bendDeltaM: number; // change in pipe length from filleting corners (≤ 0)
  elbowVertices: number[]; // vertex indices needing a corner fitting
  bends: number; // natural (curved) bends
};

// Build the path a pipe actually follows: gentle corners become tangent arcs of the
// pipe's min bend radius; corners too sharp (or rigid pipe) stay sharp and need an elbow.
function computePipePath(
  latlngs: [number, number][],
  opts: { hasPipe: boolean; radiusM: number | null; maxBendDeg: number | null },
): PipePathResult {
  const n = latlngs.length;
  if (!opts.hasPipe || n < 3) {
    return { path: latlngs.slice(), bendDeltaM: 0, elbowVertices: [], bends: 0 };
  }
  const rigid = opts.radiusM == null || opts.radiusM <= 0;
  const R = rigid ? 0 : (opts.radiusM as number);
  const maxDeg = opts.maxBendDeg ?? 180;

  // Local planar frame in metres around the first vertex (bends are small/local).
  const lat0 = latlngs[0]![0];
  const lng0 = latlngs[0]![1];
  const mLat = 111320;
  const mLng = 111320 * Math.cos((lat0 * Math.PI) / 180) || 1;
  const toXY = (p: [number, number]): [number, number] => [(p[1] - lng0) * mLng, (p[0] - lat0) * mLat];
  const toLL = (x: number, y: number): [number, number] => [lat0 + y / mLat, lng0 + x / mLng];
  const P = latlngs.map(toXY);

  const path: [number, number][] = [latlngs[0]!];
  const elbowVertices: number[] = [];
  let bendDeltaM = 0;
  let bends = 0;

  for (let i = 1; i < n - 1; i++) {
    const A = P[i - 1]!, V = P[i]!, C = P[i + 1]!;
    const inX = V[0] - A[0], inY = V[1] - A[1];
    const outX = C[0] - V[0], outY = C[1] - V[1];
    const li = Math.hypot(inX, inY), lo = Math.hypot(outX, outY);
    if (li < 1e-6 || lo < 1e-6) continue;
    const ix = inX / li, iy = inY / li;
    const ox = outX / lo, oy = outY / lo;
    const dot = Math.max(-1, Math.min(1, ix * ox + iy * oy));
    const defl = Math.acos(dot); // 0 = straight
    const deflDeg = (defl * 180) / Math.PI;
    if (deflDeg < 0.5) continue; // collinear — nothing to do

    const T = R > 0 ? R * Math.tan(defl / 2) : 0;
    const fits = !rigid && T < 0.49 * Math.min(li, lo);
    if (rigid || deflDeg > maxDeg || !fits) {
      elbowVertices.push(i);
      path.push(latlngs[i]!); // sharp corner — elbow handles the turn
      continue;
    }

    // Tangent arc of radius R between the two segments.
    const pinX = V[0] - T * ix, pinY = V[1] - T * iy;
    const cross = ix * oy - iy * ox;
    const sign = cross >= 0 ? 1 : -1;
    const nx = sign > 0 ? -iy : iy;
    const ny = sign > 0 ? ix : -ix;
    const ox0 = pinX + R * nx, oy0 = pinY + R * ny; // arc centre
    const a0 = Math.atan2(pinY - oy0, pinX - ox0);
    path.push(toLL(pinX, pinY));
    const steps = Math.max(4, Math.round(defl / (Math.PI / 24)));
    for (let s = 1; s <= steps; s++) {
      const a = a0 + sign * defl * (s / steps);
      path.push(toLL(ox0 + R * Math.cos(a), oy0 + R * Math.sin(a)));
    }
    bendDeltaM += R * defl - 2 * T; // arc shorter than going to the corner and back
    bends++;
  }
  path.push(latlngs[n - 1]!);
  return { path, bendDeltaM, elbowVertices, bends };
}

// swisstopo approximate WGS84 → LV95 (EPSG:2056). Accurate to ~1 m, includes the
// CH1903↔WGS84 datum shift. Source: swisstopo "Approximate formulas".
function wgs84ToLv95(lat: number, lon: number): [number, number] {
  const phi = (lat * 3600 - 169028.66) / 10000;
  const lam = (lon * 3600 - 26782.5) / 10000;
  const E =
    2600072.37 + 211455.93 * lam - 10938.51 * lam * phi - 0.36 * lam * phi * phi - 44.54 * lam ** 3;
  const N =
    1200147.07 +
    308807.95 * phi +
    3745.25 * lam ** 2 +
    76.63 * phi ** 2 -
    194.56 * lam ** 2 * phi +
    119.79 * phi ** 3;
  return [E, N];
}

// LV95 bounding box of Switzerland (generous) — skip the API call outside it.
function inSwitzerland(coords: [number, number][]): boolean {
  return coords.every(
    ([e, n]) => e > 2450000 && e < 2850000 && n > 1050000 && n < 1320000,
  );
}

// swisstopo approximate LV95 (EPSG:2056) → WGS84 [lat, lon]. Inverse of wgs84ToLv95.
function lv95ToWgs84(E: number, N: number): [number, number] {
  const y = (E - 2600000) / 1000000;
  const x = (N - 1200000) / 1000000;
  const lon =
    (2.6779094 + 4.728982 * y + 0.791484 * y * x + 0.1306 * y * x * x - 0.0436 * y ** 3) * (100 / 36);
  const lat =
    (16.9023892 + 3.238272 * x - 0.270978 * y ** 2 - 0.002528 * x ** 2 - 0.0447 * y ** 2 * x - 0.0140 * x ** 3) *
    (100 / 36);
  return [lat, lon];
}

export type TerrainResult = {
  terrain: number;
  minAlt: number;
  maxAlt: number;
  minPt: [number, number];
  maxPt: [number, number];
};

// Terrain-following (3D) length + elevation extremes for a line via the swisstopo
// elevation profile. Returns null outside coverage / on any error (caller falls back to planar).
async function fetchTerrain(latlngs: [number, number][]): Promise<TerrainResult | null> {
  if (latlngs.length < 2) return null;
  const coords = latlngs.map(([lat, lng]) => wgs84ToLv95(lat, lng));
  if (!inSwitzerland(coords)) return null;
  try {
    const geom = JSON.stringify({ type: "LineString", coordinates: coords });
    const url =
      "https://api3.geo.admin.ch/rest/services/profile.json?sr=2056&distinct_points=true&nb_points=200&geom=" +
      encodeURIComponent(geom);
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const pts = (await res.json()) as { dist: number; alts: { COMB?: number }; easting: number; northing: number }[];
    if (!Array.isArray(pts) || pts.length < 2) return null;

    let terrain = 0;
    let lo = pts[0]!;
    let hi = pts[0]!;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      if (i > 0) {
        const prev = pts[i - 1]!;
        const dd = p.dist - prev.dist;
        const da = p.alts?.COMB != null && prev.alts?.COMB != null ? p.alts.COMB - prev.alts.COMB : 0;
        terrain += Math.sqrt(dd * dd + da * da);
      }
      if ((p.alts?.COMB ?? Infinity) < (lo.alts?.COMB ?? Infinity)) lo = p;
      if ((p.alts?.COMB ?? -Infinity) > (hi.alts?.COMB ?? -Infinity)) hi = p;
    }
    if (lo.alts?.COMB == null || hi.alts?.COMB == null || terrain <= 0) return null;
    return {
      terrain,
      minAlt: lo.alts.COMB,
      maxAlt: hi.alts.COMB,
      minPt: lv95ToWgs84(lo.easting, lo.northing),
      maxPt: lv95ToWgs84(hi.easting, hi.northing),
    };
  } catch {
    return null;
  }
}

function genId(): string {
  return Math.random().toString(36).slice(2, 9);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function wireSearch(input: HTMLInputElement, results: HTMLElement, map: L.Map) {
  let timer: number | undefined;
  let lastQuery = "";

  const hide = () => {
    results.classList.add("hidden");
    results.innerHTML = "";
  };

  input.addEventListener("input", () => {
    const q = input.value.trim();
    window.clearTimeout(timer);
    if (q.length < 3) {
      hide();
      return;
    }
    timer = window.setTimeout(() => void run(q), 400);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hide();
      input.blur();
    }
  });

  document.addEventListener("click", (e) => {
    if (e.target !== input && !results.contains(e.target as Node)) hide();
  });

  async function run(q: string) {
    if (q === lastQuery) return;
    lastQuery = q;
    try {
      const url =
        "https://nominatim.openstreetmap.org/search?format=json&limit=5&q=" +
        encodeURIComponent(q);
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const items = (await res.json()) as { display_name: string; lat: string; lon: string }[];
      if (items.length === 0) {
        results.innerHTML = `<li class="px-3 py-2 text-slate-400">No results</li>`;
        results.classList.remove("hidden");
        return;
      }
      results.innerHTML = items
        .map(
          (it) =>
            `<li><button type="button" data-lat="${it.lat}" data-lon="${it.lon}"
               class="block w-full truncate px-3 py-2 text-left hover:bg-slate-100">${escapeHtml(
                 it.display_name,
               )}</button></li>`,
        )
        .join("");
      results.classList.remove("hidden");
    } catch {
      /* network / rate-limit — ignore */
    }
  }

  results.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-lat]");
    if (!btn) return;
    const lat = Number(btn.dataset.lat);
    const lon = Number(btn.dataset.lon);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      map.flyTo([lat, lon], 19);
      hide();
      input.value = "";
    }
  });
}

// Load MapLibre GL + the Leaflet glue from CDN once. The glue extends the global
// `L`, so we expose our bundled Leaflet there first. Resolves when L.maplibreGL exists.
let maplibrePromise: Promise<void> | null = null;
function loadMapLibre(): Promise<void> {
  if (maplibrePromise) return maplibrePromise;
  maplibrePromise = new Promise<void>((resolve, reject) => {
    if ((L as any).maplibreGL) return resolve();
    (window as any).L = L; // glue attaches L.maplibreGL to this instance
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
    document.head.appendChild(css);
    const addScript = (src: string) =>
      new Promise<void>((res, rej) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => res();
        s.onerror = () => rej(new Error("load failed: " + src));
        document.head.appendChild(s);
      });
    addScript("https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js")
      .then(() => addScript("https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.22/leaflet-maplibre-gl.js"))
      .then(() => ((L as any).maplibreGL ? resolve() : reject(new Error("maplibreGL missing"))))
      .catch(reject);
  });
  return maplibrePromise;
}

function injectLeafletCss() {
  const links = [
    "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css",
    "https://unpkg.com/@geoman-io/leaflet-geoman-free@2.18.3/dist/leaflet-geoman.css",
  ];
  for (const href of links) {
    if (document.querySelector(`link[href="${href}"]`)) continue;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
}
